import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { cx } from './cx';
import { wfNavStyle } from '@/styles/typography';
import { ANIMATION_REGISTRY } from '@/config/animationRegistry';

export interface TopTabItem {
  id: string;
  label: string;
  /** Optional spotlight target for post-login / page tours. */
  tourId?: string;
}

export interface TopTabsProps {
  tabs: TopTabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** Increment after login — stagger reveal left → right. */
  waveGeneration?: number;
  onAppearComplete?: () => void;
}

/** Wireframe `.nav` / `.nava` — scaled via --type-nav, gap from .hd */
export default function TopTabs({
  tabs,
  activeId,
  onChange,
  className,
  waveGeneration = 0,
  onAppearComplete,
}: TopTabsProps) {
  const appearWave = waveGeneration > 0 && tabs.length >= 4;
  const { staggerMs, postLoginDelayMs } = ANIMATION_REGISTRY.navTabsWave;
  const [revealedIndex, setRevealedIndex] = useState(() => tabs.length - 1);
  const playedGenRef = useRef(0);

  useEffect(() => {
    if (!appearWave) {
      // Guest nav (1 tab) — keep home visible; do NOT flash all tabs visible during login transition.
      if (tabs.length < 4) setRevealedIndex(tabs.length - 1);
      return;
    }
    if (playedGenRef.current === waveGeneration) return;
    playedGenRef.current = waveGeneration;

    setRevealedIndex(-1);
    const timers: number[] = [];

    timers.push(
      window.setTimeout(() => {
        for (let i = 0; i < tabs.length; i++) {
          timers.push(
            window.setTimeout(() => {
              setRevealedIndex(i);
              if (i === tabs.length - 1) onAppearComplete?.();
            }, i * staggerMs)
          );
        }
      }, postLoginDelayMs)
    );

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [appearWave, waveGeneration, tabs.length, staggerMs, postLoginDelayMs, onAppearComplete]);

  return (
    <nav
      className={cx('flex items-center min-w-0', className)}
      style={{ gap: 'var(--proto-hd-gap)' }}
      role="tablist"
    >
      {tabs.map((tab, index) => {
        const active = tab.id === activeId;
        const isRevealed = index <= revealedIndex;
        const tabStyle: CSSProperties = {
          ...wfNavStyle,
          opacity: isRevealed ? 1 : 0,
          transform: isRevealed ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.82)',
          transition: appearWave
            ? 'opacity 380ms cubic-bezier(0.22, 1, 0.36, 1), transform 380ms cubic-bezier(0.22, 1, 0.36, 1)'
            : undefined,
          pointerEvents: isRevealed ? undefined : 'none',
        };

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-hidden={appearWave && !isRevealed ? true : undefined}
            data-tour-id={tab.tourId}
            onClick={() => onChange(tab.id)}
            className={cx(
              /* Match header chip height so label baselines sit with logo / actions */
              'relative inline-flex items-center justify-center h-[var(--proto-hd-chip-h)] font-ui whitespace-nowrap leading-none',
              'focus-visible:outline-none focus-visible:shadow-focus-ring',
              !appearWave && 'transition-colors duration-150',
              active ? 'font-bold text-ink' : 'font-normal text-caption-muted hover:text-ice'
            )}
            style={tabStyle}
          >
            {tab.label}
            <span
              aria-hidden
              className={cx(
                'absolute left-0 right-0 bottom-0 h-[2px] rounded-full origin-left transition-all duration-200',
                active ? 'cosmos-beam opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}
